import React, { useCallback, useEffect, useRef, useState } from 'react';
import axios from 'axios';
import { Link, useLocation } from 'react-router-dom';
import { MessageSquare, X, Maximize2, Plus } from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import { API_URL } from './chatShared';
import { ChatThread } from './ChatThread';
import { ChatConvItem } from './ChatConvItem';
import { ChatAvatar } from './ChatAvatar';
import { NewChatDialog } from './NewChatDialog';

const SS = 'chat_dock';
const readState = () => { try { return JSON.parse(sessionStorage.getItem(SS)) || {}; } catch { return {}; } };

const Peek = ({ peek, onOpen, onClose }) => (
  <div className="absolute bottom-16 right-0 w-72 rounded-2xl bg-white shadow-xl border border-slate-200 p-3 flex gap-2 items-start animate-in slide-in-from-bottom-2 fade-in duration-200" data-testid="chat-dock-peek">
    <ChatAvatar name={peek.sender} url={peek.avatar} className="h-9 w-9" />
    <button type="button" onClick={onOpen} className="flex-1 min-w-0 text-left" data-testid="chat-dock-peek-open">
      <p className="text-sm font-semibold text-slate-800 truncate">{peek.title}</p>
      <p className="text-xs text-slate-500 line-clamp-2">{peek.text}</p>
    </button>
    <button type="button" onClick={onClose} className="text-slate-400 hover:text-slate-700" data-testid="chat-dock-peek-close"><X className="w-4 h-4" /></button>
  </div>
);

// Chat rápido: botão flutuante em todas as páginas, abre painel sem sair de onde está
export const ChatDock = ({ unread }) => {
  const { user } = useAuth();
  const { pathname } = useLocation();
  const [open, setOpen] = useState(() => !!readState().open);
  const [activeId, setActiveId] = useState(() => readState().c || null);
  const [convs, setConvs] = useState(null);
  const [peek, setPeek] = useState(null);
  const [newOpen, setNewOpen] = useState(false);
  const [contacts, setContacts] = useState([]);
  const prevUnread = useRef(null);
  const peekTimer = useRef(null);

  useEffect(() => { sessionStorage.setItem(SS, JSON.stringify({ open, c: activeId })); }, [open, activeId]);
  const loadConvs = useCallback(() => axios.get(`${API_URL}/chat/conversations`).then((r) => { setConvs(r.data); return r.data; }).catch(() => []), []);

  useEffect(() => {
    if (unread === null) return;
    if (prevUnread.current !== null && unread > prevUnread.current) {
      loadConvs().then((list) => {
        const c = list.filter((x) => x.unread > 0 && x.is_member).sort((a, b) => String(b.last_message_at).localeCompare(String(a.last_message_at)))[0];
        if (!c || (open && activeId === c.id)) return;
        const [sender, ...rest] = (c.last_message_preview || '').split(': ');
        const other = c.members.find((m) => m.id !== user?.id) || {};
        setPeek({ id: c.id, title: c.name, sender: c.type === 'direct' ? other.name : sender, avatar: c.type === 'direct' ? other.avatar_url : null, text: rest.join(': ') || c.last_message_preview });
        clearTimeout(peekTimer.current);
        peekTimer.current = setTimeout(() => setPeek(null), 8000);
      });
    }
    prevUnread.current = unread;
  }, [unread, loadConvs, open, activeId, user?.id]);
  useEffect(() => {
    if (!open) return undefined;
    loadConvs();
    const t = setInterval(loadConvs, 10000);
    return () => clearInterval(t);
  }, [open, loadConvs]);
  useEffect(() => () => clearTimeout(peekTimer.current), []);

  if (!user || ['angariador', 'parceiro'].includes(user.role) || pathname.startsWith('/chat')) return null;
  const active = (convs || []).find((c) => c.id === activeId);
  const openConv = (id) => { setActiveId(id); setOpen(true); setPeek(null); };
  const openNew = () => { setNewOpen(true); if (!contacts.length) axios.get(`${API_URL}/chat/contacts`).then((r) => setContacts(r.data)).catch(() => {}); };

  return (
    <div className="fixed bottom-5 right-5 z-50 print:hidden" data-testid="chat-dock">
      {open && (
        <div className="fixed inset-0 sm:inset-auto sm:bottom-24 sm:right-5 sm:w-[400px] sm:h-[min(640px,calc(100vh-130px))] bg-white sm:rounded-2xl shadow-2xl border border-slate-200 overflow-hidden flex flex-col animate-in slide-in-from-bottom-4 fade-in duration-200" data-testid="chat-dock-panel">
          {active ? (
            <ChatThread key={active.id} conv={active} me={user} compact onClose={() => setOpen(false)} onBack={() => setActiveId(null)} onActivity={loadConvs} onOpenConv={(id) => { loadConvs(); setActiveId(id); }} />
          ) : (
            <>
              <div className="flex items-center gap-2 px-3 py-3 border-b border-slate-100">
                <p className="flex-1 font-bold font-[Outfit] text-[#2D3E6F] flex items-center gap-2"><MessageSquare className="w-5 h-5" />Chat</p>
                <button type="button" onClick={openNew} title="Nova conversa" className="p-1.5 rounded-full bg-[#2D3E6F] text-white" data-testid="chat-dock-new"><Plus className="w-4 h-4" /></button>
                <Link to="/chat" title="Abrir página do chat" className="p-1.5 text-slate-500 hover:text-slate-800" data-testid="chat-dock-fullpage"><Maximize2 className="w-4 h-4" /></Link>
                <button type="button" onClick={() => setOpen(false)} title="Fechar" className="p-1.5 text-slate-500 hover:text-slate-800" data-testid="chat-dock-close"><X className="w-4 h-4" /></button>
              </div>
              <div className="flex-1 overflow-y-auto divide-y divide-slate-50">
                {convs === null && <p className="p-4 text-sm text-slate-400">A carregar conversas…</p>}
                {convs?.length === 0 && <p className="p-4 text-sm text-slate-400">Sem conversas. Carregue em + para começar.</p>}
                {(convs || []).map((c) => <ChatConvItem key={c.id} c={c} me={user} prefix="chat-dock" onSelect={setActiveId} />)}
              </div>
            </>
          )}
        </div>
      )}
      {peek && !open && <Peek peek={peek} onOpen={() => openConv(peek.id)} onClose={() => setPeek(null)} />}
      <button type="button" onClick={() => { setOpen((o) => !o); setPeek(null); }} title={open ? 'Fechar chat' : 'Abrir chat'}
        className={`relative w-14 h-14 rounded-full shadow-xl flex items-center justify-center text-white transition-transform hover:scale-105 ${open ? 'bg-slate-700 hidden sm:flex' : 'bg-[#2D3E6F]'} ${peek ? 'animate-bounce' : ''}`} data-testid="chat-dock-btn">
        {open ? <X className="w-6 h-6" /> : <MessageSquare className="w-6 h-6" />}
        {!open && unread > 0 && <span className="absolute -top-1 -right-1 min-w-[22px] h-[22px] px-1 rounded-full bg-rose-500 text-white text-xs font-bold flex items-center justify-center border-2 border-white" data-testid="chat-dock-unread">{unread > 99 ? '99+' : unread}</span>}
      </button>
      <NewChatDialog open={newOpen} onClose={() => setNewOpen(false)} contacts={contacts} canGroup={['super_admin', 'gestor', 'chefe_equipa'].includes(user.role)}
        onCreated={(id) => { loadConvs(); openConv(id); }} />
    </div>
  );
};
